import { ChangeEvent, FC, ReactElement, ReactNode, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { deleteUtkastSammansattReferens, patchUtkastSammansattReferens, postUtkast } from 'store/utkastSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaCheckbox, RaaDialog, RaaSpinner } from 'raa-react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';
import { GeometryFileUploadInput } from 'components/geometryFileUpload/GeometryFileUpload';
import { TextButton } from 'components/sharedElements/sharedElements';
import DeleteIngaendeUtkastDialog from 'components/utkast/deleteIngaendeUtkastDialog/DeleteIngaendeUtkastDialog';
import UtkastList from 'components/utkastList/UtkastList';

import { BebyggelseobjektPost, BebyggelseobjektUtkastIndex, HTTP_STATUS } from 'models';

import { BebyggelseobjektgruppEnum, MapModeEnum, SkyddsklassningstypEnum } from 'enums';

import { useReserveBebyggelseutkastMutation } from 'api/endpoints/reservationSlice';
import { FeatureCollection } from 'geojson';
import { reserveMultiple } from 'utilities/reserveUtilities';

export const SkyddsklassningSammansattUtkastObjektSection: FC = (): ReactNode => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setDialog, unsetDialog } = useDialog();

  const [convertingFile, setConvertingFile] = useState<boolean>(false);
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const [utkastBeingUpdated, setUtkastBeingUpdated] = useState<string[]>([]);

  const user = useAppSelector((state: RootState) => state.user.data);
  const aktivitet = useAppSelector((state: RootState) => state.aktivitet);
  const originalBO = useAppSelector((state: RootState) => state.bebyggelseobjekt);
  const bebyggelseobjektUtkast = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast);
  const skyddsklassningsAktivitet = aktivitet.data?.skyddsklassning;
  const utkastsWithErrors = aktivitet.error?.error_fields;

  const sammansattUtkast = useMemo(() => {
    return bebyggelseobjektUtkast.data?.find((x) => x.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT);
  }, [bebyggelseobjektUtkast.data]);

  const ingaendeUtkast = useMemo(() => {
    return bebyggelseobjektUtkast.data?.filter((x) => x.bebyggelseobjektgrupp !== BebyggelseobjektgruppEnum.SAMMANSATT);
  }, [bebyggelseobjektUtkast.data]);

  const isNyttBeslut: boolean =
    skyddsklassningsAktivitet?.skyddsklassningstyp_id === SkyddsklassningstypEnum.BESLUT_OM_STATLIGT_BYGGNADSMINNE ||
    skyddsklassningsAktivitet?.skyddsklassningstyp_id === SkyddsklassningstypEnum.BESLUT_OM_BYGGNADSMINNE;

  const isHavning =
    skyddsklassningsAktivitet?.skyddsklassningstyp_id === SkyddsklassningstypEnum.HAVNING_AV_STATLIGT_BYGGNADSMINNE ||
    skyddsklassningsAktivitet?.skyddsklassningstyp_id === SkyddsklassningstypEnum.HAVNING_AV_BYGGNADSMINNE;

  const isRevidering: boolean =
    skyddsklassningsAktivitet?.skyddsklassningstyp_id ===
      SkyddsklassningstypEnum.REVIDERING_AV_STATLIGT_BYGGNADSMINNE ||
    skyddsklassningsAktivitet?.skyddsklassningstyp_id === SkyddsklassningstypEnum.REVIDERING_AV_BYGGNADSMINNE;

  const isAgarbyteSbm: boolean =
    skyddsklassningsAktivitet?.skyddsklassningstyp_id === SkyddsklassningstypEnum.AGARBYTE_STATLIGT_BYGGNADSMINNE;

  const sammansattUtkastIsRedigerbar = sammansattUtkast?.registrering?.registreringsstatustyp_id === 'REDIGERBAR';

  const handleInteractionProceed = async (geometri: FeatureCollection<any>) => {
    if (skyddsklassningsAktivitet?.aktivitet_id) {
      const bebyggelseobjektNyPost: BebyggelseobjektPost = {
        aktivitet_id: skyddsklassningsAktivitet.aktivitet_id,
        bebyggelseobjektgrupp: BebyggelseobjektgruppEnum.SAMMANSATT,
        geometri: geometri
      };
      try {
        const result = await dispatch(postUtkast(bebyggelseobjektNyPost)).unwrap();
        dispatch(getBebyggelseobjektUtkast(skyddsklassningsAktivitet.aktivitet_id));
        unsetDialog();
        navigate(`/utkast/${result.utkast_id}/redigera`);
      } catch (error: any) {
        return Promise.reject(error);
      }
    }
  };

  const updateIngaendeParentReferens = async (removeRef: boolean, utkast: BebyggelseobjektUtkastIndex) => {
    if (!sammansattUtkast?.utkast_id) return;

    return removeRef && utkast.sammansatt_parent_id
      ? dispatch(
          deleteUtkastSammansattReferens({
            utkast_id: utkast.sammansatt_parent_id,
            bebyggelseobjekt_id: utkast.utkast_id
          })
        )
      : dispatch(
          patchUtkastSammansattReferens({
            utkast_id: sammansattUtkast.utkast_id,
            body: {
              bebyggelseobjekt_id: [utkast.utkast_id]
            }
          })
        );
  };

  const [, results] = useReserveBebyggelseutkastMutation({ fixedCacheKey: 'utkast-reservation' });
  const { error: reserveraError } = results;
  const reserveraErrorRef = useRef<FetchBaseQueryError | SerializedError | undefined>();
  reserveraErrorRef.current = reserveraError;

  const handleIngaendeCheckBoxChange = async (
    event: ChangeEvent<HTMLInputElement>,
    utkast: BebyggelseobjektUtkastIndex
  ) => {
    if (!sammansattUtkast?.utkast_id) return;

    setUtkastBeingUpdated((prev) => [...prev, utkast.utkast_id]);
    const remove = event.currentTarget.checked;

    await reserveMultiple([sammansattUtkast.utkast_id, utkast.utkast_id], true);

    if (reserveraErrorRef.current && 'status' in reserveraErrorRef.current) {
      // TODO Här kommer vi att behöva reseta checkboxes om något error sker. Nu kommer den förbli checked trots att något har gått fel.

      if (reserveraErrorRef.current.status === HTTP_STATUS.NOT_FOUND) {
        setDialog(RaaDialog, {
          header: 'Något gick fel',
          close: unsetDialog,
          cancelButtonText: 'stäng',
          cancel: unsetDialog,
          ariaDescribedBy: 'dialog-description',
          children: (
            <p id="dialog-description" className="tw-mt-8">
              {(reserveraErrorRef.current?.data as any).message}
            </p>
          )
        });
      }

      if (reserveraErrorRef.current.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
        openLockedUtkastDialog(utkast);
      }
    } else {
      const response = await updateIngaendeParentReferens(remove, utkast);
      await reserveMultiple([sammansattUtkast.utkast_id, utkast.utkast_id], false);

      if (response?.meta.requestStatus === 'rejected') {
        setDialog(RaaDialog, {
          header: 'Något gick fel',
          close: unsetDialog,
          cancelButtonText: 'stäng',
          cancel: unsetDialog,
          ariaDescribedBy: 'dialog-description',
          children: (
            <p id="dialog-description" className="tw-mt-8">
              Bebyggelseobjektet kunde inte redigeras.
            </p>
          )
        });
      }
    }
    setUtkastBeingUpdated((prev) => prev.filter((p) => p !== utkast.utkast_id));
  };

  const openLockedUtkastDialog = (utkast: BebyggelseobjektUtkastIndex) => {
    setDialog(RaaDialog, {
      header: 'Utkastet är låst',
      close: unsetDialog,
      cancelButtonText: 'stäng',
      cancel: unsetDialog,
      ariaDescribedBy: 'dialog-description',
      children: (
        <p id="dialog-description" className="tw-mt-8">
          Utkastet redigeras av {utkast.registrering.reservation.reserverad_av_anvandarnamn} och kan inte ändras av
          någon annan.
        </p>
      )
    });
  };

  const getUtkastAction = (utkast: BebyggelseobjektUtkastIndex): ReactElement => {
    const isNewToSkyddsklassning = originalBO.data?.sammansatt?.ingaendeobjekt.every((i) => i.id !== utkast.utkast_id);

    const isReserved: boolean =
      !!utkast.registrering.reservation.reserverad_av_anvandarid &&
      utkast.registrering.reservation.reserverad_av_anvandarid !== user?.username;

    if (isReserved) {
      return (
        <button
          aria-label="utkast låst"
          className={'raa-icon raa-icon-lock-closed tw-p-2 tw-text-raa-gray-5'}
          onClick={() => openLockedUtkastDialog(utkast)}
        />
      );
    }

    if (isHavning || isAgarbyteSbm) {
      return <></>;
    }

    if (isRevidering && !isNewToSkyddsklassning) {
      return (
        <RaaCheckbox
          disabled={isReserved || utkastBeingUpdated.includes(utkast.utkast_id)}
          labelContent={'Ta bort ur SBM'}
          defaultChecked={!utkast?.sammansatt_parent_id}
          onChange={(event) => handleIngaendeCheckBoxChange(event, utkast)}
        />
      );
    }

    return (
      <button
        aria-label="radera utkast"
        className={'raa-icon raa-icon-delete tw-p-2 tw-text-raa-orange'}
        onClick={() => setDialog(DeleteIngaendeUtkastDialog, utkast)}
      />
    );
  };

  return (
    <DrawerSection>
      <h2 className="tw-heading-md">Berörda objekt</h2>
      {aktivitet.error && <p className="tw-text-raa-error-red">{aktivitet.error.message}</p>}
      <div className="tw-my-4">
        <h3 className="tw-heading-sm tw-font-semibold">{aktivitet.data?.skyddsklassning?.skyddsklassningstyp_namn}</h3>
        {convertingFile && (
          <div className="tw-flex tw-justify-center tw-py-8">
            <RaaSpinner size="3rem" />
          </div>
        )}
        <UtkastList
          list={sammansattUtkast && [sammansattUtkast]}
          error={bebyggelseobjektUtkast.error}
          user={user}
          requestStatus={bebyggelseobjektUtkast.requestStatus}
          utkastsWithErrors={utkastsWithErrors}
        />

        {!bebyggelseobjektUtkast.data?.length && (
          <>
            <TextButton
              icon="raa-icon-add"
              onClick={() =>
                setDialog(RaaDialog, {
                  header: 'Ladda upp geometrin',
                  cancelButtonText: 'Avbryt',
                  cancel: () => unsetDialog(),
                  proceedButtonText: 'Välj fil',
                  proceed: () => fileUploadRef.current?.click(),
                  ariaDescribedBy: 'dialog-description',
                  children: (
                    <p id="dialog-description" className="tw-mb-4 tw-mt-10 tw-text-center">
                      Filformat: GeoJSON eller GeoPackage
                    </p>
                  )
                })
              }
            >
              Registrera
            </TextButton>
            <GeometryFileUploadInput
              id="sammansattGeoJsonInput"
              converting={(converting: boolean) => {
                unsetDialog();
                setDialog(RaaDialog, {
                  header: 'Ladda upp geometrin',
                  proceedButtonText: 'Laddar filen...',
                  ariaDescribedBy: 'dialog-description',
                  children: (
                    <>
                      <div className="tw-mb-8 tw-mt-6 tw-flex tw-justify-center">
                        <RaaSpinner />
                      </div>
                      <p id="dialog-description" className="tw-mb-6 tw-text-center">
                        Laddar upp fil...
                      </p>
                    </>
                  )
                });
                setConvertingFile(converting);
              }}
              success={(features: any) => handleInteractionProceed(features)}
              fileUploadRef={fileUploadRef}
            />
          </>
        )}
      </div>

      {sammansattUtkast && (
        <div className="tw-my-4">
          <h3 className="tw-heading-sm tw-font-semibold">Ingående bebyggelse</h3>
          {(isRevidering || isNyttBeslut) && sammansattUtkastIsRedigerbar && (
            <TextButton icon="raa-icon-add" onClick={() => dispatch(setMapMode(MapModeEnum.ADD_UTKAST_TO_SAMMANSATT))}>
              Lägg till objekt
            </TextButton>
          )}
          <UtkastList
            list={ingaendeUtkast}
            error={bebyggelseobjektUtkast.error}
            user={user}
            requestStatus={bebyggelseobjektUtkast.requestStatus}
            action={getUtkastAction}
            utkastsWithErrors={utkastsWithErrors}
          />
        </div>
      )}
    </DrawerSection>
  );
};
